/* ============================================================================
   Parang Blue — the header submenu's behaviour. Revision 25.

   The panel opens and closes in CSS. pb-header.css shows it for
   `.pb-nav__item--parent:hover` and for a parent whose anchor carries
   aria-expanded="true", which means the pointer half of the interaction works with
   this file blocked, cached stale or never loaded at all. What is left here is the
   part CSS genuinely cannot do:

     1. aria-expanded. A parent that advertises a popup has to say whether the popup
        is open. :hover cannot write an attribute, so the state is mirrored here.
     2. Escape. Closing a panel the pointer is still sitting on means overriding a
        :hover rule, which is what the --esc class does; it is dropped again as soon
        as the pointer leaves, so the next hover opens normally.
     3. Opening on focus, which is what makes Tab work. The panel is visibility:hidden
        when closed and hidden elements are not tabbable, so if the panel only opened
        on hover, Tab from Shop would skip straight to About. Focus landing on the
        parent opens the panel first, and the panel's links are the next stops in DOM
        order, so the tour is Shop, its three links, About, its four, and on.

   WHY EVERY LISTENER IS ON document
   Horizon re-renders the header through the Section Rendering API — a soft navigation
   replaces #MainContent, but a cart or search interaction can replace the header block
   itself. Listeners bound to the items would be thrown away with them, and this file is
   a module, so the browser will not run it a second time to rebind. Delegated listeners
   on document survive any number of re-renders and cost one handler each.

   WHY THERE IS NO CLICK HANDLER
   Because a click must navigate. Shop and About are plain anchors with real hrefs and
   nothing here touches click, pointerdown or preventDefault, so the click that opens a
   dropdown on most stores is, here, simply the click that goes to /collections/all.
   ========================================================================= */

const ITEM = '.pb-nav__item--parent';
const LINK = '.menu-list__link';

/* ---------------------------------------------------------------------------
   RETIRING HORIZON'S OVERFLOW DISCLOSURE

   The five labels sit inside <overflow-list>, which hides any item that does not
   fit and draws a "More" button in its place. That button is the dropdown revision
   13 removed, and revision 24 spent a gap ramp keeping the bar narrow enough that it
   never appeared. With five labels instead of four the ramp alone cannot do it, for
   a reason that is worth naming: the component measures by putting its More slot
   first and asking which items still land on the first row, so the button's own
   width is subtracted from the budget whether or not it is ever drawn. Measured on
   this store that is 44px at 750 and 67px at 900 — enough, on its own, to push
   Contact off a bar the five labels fitted on with 17px to spare.

   showAll() is the component's own public way out. It moves every item back to the
   visible slot, sets disabled, and the shadow stylesheet's :host([disabled]) rule
   takes the More slot out of the layout, so it stops costing anything. Its
   attributeChangedCallback then calls the private reset, which disconnects the
   resize and mutation observers — and the intersection observer has already
   disconnected itself by the time it has reflowed once. After that nothing can put
   the disclosure back.

   WHY IT WAITS
   showAll() reads refs that only exist once the component has initialised, and a
   reflow that is already scheduled would undo an early call. --overflow-count is
   written onto the host by the reflow itself, so its presence is proof the component
   has finished its first pass and this is a safe moment. The observer below re-arms
   the sweep when the Section Rendering API swaps a fresh header in, which is the one
   way a second, unretired <overflow-list> can appear in the page.

   None of this is load-bearing for the labels themselves: with the module blocked the
   component is never defined at all, every item stays in the default slot, and the
   ramp in pb-header.css is what keeps the run inside its column.
   ------------------------------------------------------------------------ */

function retireOverflow() {
  let pending = 0;
  for (const el of document.querySelectorAll('#header-component overflow-list')) {
    if (el.dataset.pbOverflow === 'off') continue;
    if (typeof el.showAll !== 'function' || el.style.getPropertyValue('--overflow-count') === '') {
      pending += 1;
      continue;
    }
    try {
      el.showAll();
      el.dataset.pbOverflow = 'off';
    } catch (e) {
      pending += 1;
    }
  }
  return pending;
}

// One poll loop at a time, however many times it is armed. Four seconds is far longer
// than the component takes to reach its first reflow; the loop stops the moment there is
// nothing left unretired, so on a normal load it runs once or twice.
let sweeping = false;
function sweepOverflow() {
  if (sweeping) return;
  sweeping = true;
  let tries = 0;
  const tick = () => {
    tries += 1;
    if (retireOverflow() === 0 || tries > 40) {
      sweeping = false;
      return;
    }
    setTimeout(tick, 100);
  };
  tick();
}

sweepOverflow();
const headerGroup = document.getElementById('header-group') || document.body;
if (headerGroup) {
  new MutationObserver(sweepOverflow).observe(headerGroup, { childList: true, subtree: true });
}

/**
 * Open or close one parent. --esc is deliberately not cleared here: it is the flag that
 * says "this panel was dismissed with Escape and must stay shut", and the two things that
 * clear it are the pointer leaving the item and focus leaving it. If opening cleared it,
 * the focus() call at the end of the Escape handler below would land back on the parent,
 * raise focusin, and reopen the panel the visitor had just dismissed — which is exactly
 * what the first cut of this file did.
 * @param {Element} item @param {boolean} on
 */
function setOpen(item, on) {
  const a = item.querySelector(':scope > ' + LINK);
  if (a) a.setAttribute('aria-expanded', on ? 'true' : 'false');
  item.classList.toggle('pb-nav__item--open', on);
}

/** @param {Element} item */
function dismissed(item) {
  return item.classList.contains('pb-nav__item--esc');
}

/** Close every open parent except `keep`. @param {Element|null} keep */
function closeOthers(keep) {
  for (const el of document.querySelectorAll(ITEM + '.pb-nav__item--open')) {
    if (el !== keep) setOpen(el, false);
  }
}

/** @param {EventTarget|null} t @param {string} sel */
function closestOf(t, sel) {
  return t instanceof Element ? t.closest(sel) : null;
}

// --- pointer. pointerenter and pointerleave do not bubble, so the pair that can be
// delegated is pointerover / pointerout. pointerover on any node in the document is
// enough to decide the whole bar's state: whichever parent the pointer is inside is
// open and every other one is closed, and a pointer somewhere else on the page closes
// all of them. That also covers leaving the header entirely, which a pointerleave on
// the items would not.
document.addEventListener(
  'pointerover',
  (event) => {
    const item = closestOf(event.target, ITEM);
    closeOthers(item);
    if (item && !dismissed(item)) setOpen(item, true);
  },
  { passive: true }
);

// The Escape suppression is dropped when the pointer leaves the item it was applied to,
// so hovering away and back reopens the panel.
document.addEventListener(
  'pointerout',
  (event) => {
    const item = closestOf(event.target, ITEM);
    if (!item) return;
    const to = event.relatedTarget;
    if (to instanceof Node && item.contains(to)) return;
    item.classList.remove('pb-nav__item--esc');
  },
  { passive: true }
);

// --- focus. focusin fires for the parent anchor and for every link inside the panel;
// both resolve to the same item, so the panel stays open for the whole of its own tour
// and closes the moment focus reaches anything else.
document.addEventListener('focusin', (event) => {
  const item = closestOf(event.target, ITEM);
  closeOthers(item);
  if (item && !dismissed(item)) setOpen(item, true);
});

// focusout covers focus leaving the document altogether — clicking the page background,
// or tabbing out of the window — which produces no focusin to close on.
document.addEventListener('focusout', (event) => {
  const item = closestOf(event.target, ITEM);
  if (!item) return;
  const to = event.relatedTarget;
  if (to instanceof Node && item.contains(to)) return;
  // focus has genuinely left this item, so the Escape suppression has served its purpose:
  // tabbing back to the parent later should open the panel again like any other visit.
  item.classList.remove('pb-nav__item--esc');
  setOpen(item, false);
});

// --- Escape. Closes the panel the keyboard is in and returns focus to its parent, which
// is where a visitor expects to be after dismissing a disclosure. The --esc class holds
// the panel shut against the :hover rule until the pointer leaves.
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape' && event.key !== 'Esc') return;
  const item = closestOf(event.target, ITEM) || document.querySelector(ITEM + '.pb-nav__item--open');
  if (!item) return;
  const a = item.querySelector(':scope > ' + LINK);
  setOpen(item, false);
  item.classList.add('pb-nav__item--esc');
  if (a instanceof HTMLElement) a.focus();
});
